'use client'

import React, { useEffect, useState } from 'react'
import { Drawer, Flex, Modal, Stack, Text } from '@mantine/core'
import { IconSearch } from '@tabler/icons-react'
import { useDebouncedValue } from '@mantine/hooks'
import { useApi } from '@/contexts/ApiContext'
import { AutocompleteTextInput } from '@/components/AutoCompleteTextInput'
import { useRouter } from 'next/navigation'
import { capitalizeFirstLetter } from '@/lib/utilities'
import { useIsMobile } from '@/lib/mobileDetector'
import { useThemeColor } from '@/hooks/useThemeColor'
import { storeRoutes } from '@/lib/routes/store'
import { StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'

type SearchDrawerProps = {
    showSearchDrawer: boolean
    setShowSearchDrawer: (value: boolean) => void
    analyticsEnabled?: boolean
}

const StoreSearchDrawer: React.FC<SearchDrawerProps> = ({
    showSearchDrawer,
    setShowSearchDrawer,
    analyticsEnabled = true,
}: SearchDrawerProps) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [searching, setSearching] = useState(false)
    const [searchResults, setSearchResults] = useState<string[]>([])
    const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 500)
    const router = useRouter()
    const { apiGet } = useApi()
    const isMobile = useIsMobile()

    useThemeColor(showSearchDrawer, 'var(--gm-bg-solid)')

    useEffect(() => {
        const query = debouncedSearchTerm.trim()
        if (query === '') {
            setSearchResults([])
            return
        }

        const fetchSuggestions = async () => {
            try {
                setSearching(true)
                const response = await apiGet(`/external/search/suggestions?query=${encodeURIComponent(query)}`)
                setSearchResults(response)
                // Sends tracked event after 500 ms of stopped typing (equivalent to debounced value)
                if (analyticsEnabled) {
                    trackStoreEvent(StoreAnalytics.SEARCH_QUERY, { query, suggestions: response })
                }
            } catch (error) {
                console.error('Failed to fetch medications:', error)
                setSearchResults([])
            } finally {
                setSearching(false)
            }
        }

        fetchSuggestions()
    }, [analyticsEnabled, debouncedSearchTerm, apiGet])

    function handleSuggestionSelection(suggestion: string) {
        setShowSearchDrawer(false)
        setSearchTerm('')
        setSearchResults([])
        setSearching(false)
        router.push(storeRoutes.search(suggestion))
    }

    function handleDrawerClose() {
        setShowSearchDrawer(false)
        setSearchTerm('')
        setSearching(false)
    }

    function handleEnterKeyPress() {
        const query = searchTerm.trim()
        if (query !== '') {
            setShowSearchDrawer(false)
            setSearchTerm('')
            setSearchResults([])
            setSearching(false)
            router.push(storeRoutes.search(query))
            trackSearchEvent(query, query, 'enter_key')
        }
    }

    return isMobile ? renderMobileDrawer() : renderDesktopModal()

    function renderMobileDrawer() {
        return (
            <Drawer
                opened={showSearchDrawer}
                onClose={handleDrawerClose}
                zIndex={2000}
                position="bottom"
                size="100%"
                padding={0}
                withCloseButton={false}
                trapFocus={true}
                returnFocus={false}
                styles={{
                    content: {
                        backgroundColor: 'var(--gm-background-bg-surface)',
                        padding: 0,
                        borderRadius: 0,
                    },
                }}
                transitionProps={{
                    transition: 'slide-down',
                    duration: 500,
                    timingFunction: 'ease-in-out',
                }}
            >
                {renderSearchInput()}
                {debouncedSearchTerm.trim() === '' && renderNoResultsFound()}
                {debouncedSearchTerm.trim() !== '' && renderSuggestions()}
            </Drawer>
        )
    }

    function renderDesktopModal() {
        return (
            <Modal
                opened={showSearchDrawer}
                onClose={handleDrawerClose}
                zIndex={2000}
                trapFocus={true}
                withCloseButton={false}
                centered
                size="50%"
                transitionProps={{
                    transition: 'slide-down',
                    duration: 500,
                    timingFunction: 'ease-in-out',
                }}
                styles={{
                    content: {
                        backgroundColor: 'var(--gm-background-bg-surface)',
                    },
                }}
            >
                <Stack style={{ height: '80vh' }}>
                    {renderSearchInput()}
                    {debouncedSearchTerm.trim() === '' && renderNoResultsFound()}
                    {debouncedSearchTerm.trim() !== '' && renderSuggestions()}
                </Stack>
            </Modal>
        )
    }

    function renderSearchInput() {
        return (
            <Flex
                h={80}
                pl={16}
                pr={24}
                py={16}
                w="100%"
                align="center"
                style={{ WebkitUserSelect: 'none' }}
            >
                <AutocompleteTextInput
                    autoFocus={showSearchDrawer}
                    searching={searching}
                    placeholder={'Search'}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    suggestions={searchResults}
                    handleClose={() => {
                        setShowSearchDrawer(false)
                    }}
                    onEnterPress={handleEnterKeyPress}
                    data-testid="store-search-autocomplete"
                />
            </Flex>
        )
    }

    function renderNoResultsFound() {
        return (
            <Stack align="center" justify="center" h={'70vh'} gap={3}>
                <IconSearch size={64} />
                <Text c="dimmed" fz="22" mt="sm" ta={'center'}>
                    Search for care, labs
                    <br />
                    and prescriptions
                </Text>
            </Stack>
        )
    }

    function renderSuggestions() {
        const query = debouncedSearchTerm.trim()
        const shouldShowResults = query !== ''
        return (
            <Flex
                direction="column"
                flex={1}
                h={shouldShowResults ? '100%' : '0'}
                mah={shouldShowResults ? '75vh' : '0px'}
                style={{
                    overflow: 'hidden',
                    transition: 'max-height 500ms ease',
                }}
            >
                <Stack
                    gap={20}
                    p="16px"
                    style={{ overflow: 'auto' }}
                    onTouchMove={dismissKeyboardOnScroll}
                >
                    {searchResults.map((suggestion: string, index: number) => renderSuggestion(suggestion, index))}
                    {renderDefaultSearch()}
                </Stack>
            </Flex>
        )
    }

    function renderSuggestion(suggestion: string, index: number) {
        const query = debouncedSearchTerm.trim()
        const displayText = capitalizeFirstLetter(suggestion)
        if (!query) return <>{displayText}</>
        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
        const parts = displayText.split(regex)

        return (
            <Text
                key={suggestion + index}
                fz={24}
                fw={400}
                color={'black'}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                    handleSuggestionSelection(suggestion)
                    trackSearchEvent(query, suggestion, 'suggestion_click')
                }}
                data-testid={`search-suggestion-${index}`}
            >
                {parts.map((part, idx) =>
                    part.toLowerCase() === query.toLowerCase() ? (
                        <Text component="span" fw={700} fz={24} key={idx}>
                            {part}
                        </Text>
                    ) : (
                        <Text component="span" fz={24} key={idx}>
                            {part}
                        </Text>
                    ),
                )}
            </Text>
        )
    }

    function renderDefaultSearch() {
        const query = debouncedSearchTerm.trim()
        return (
            <Text
                fz={24}
                fw={400}
                c={'var(--Foreground-fg-highlight, var(--gm-blue-6))'}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                    handleSuggestionSelection(query)
                    trackSearchEvent(query, query, 'search_query_click')
                }}
            >
                Search &quot;{query}&quot;
            </Text>
        )
    }

    function trackSearchEvent(query: string, clicked_query: string, method: string) {
        if (!analyticsEnabled) return

        trackStoreEvent(StoreAnalytics.SUBMITTED_SEARCH, { query, clicked_query, method })
    }
}

// Drag-to-scroll on the suggestion list dismisses the mobile keyboard so the
// user can see all results. A pure tap won't fire onTouchMove, so taps to
// select a suggestion still work.
function dismissKeyboardOnScroll() {
    if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
    }
}

export default StoreSearchDrawer
